import React, { useEffect, useState } from 'react';

import Modal from '../common/Modal';
// Import the CSS file for styling
import { useWaApi } from '../../auth/helper/api/waApi';
import { validateRequiredFields } from "../../utils/formValidation";
import { deepTrim } from "../../utils/formValidation";

const WaTemplateVariablePage = () => {

  const waApi = useWaApi();
  const [templateVariables, setTemplateVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false); // State for modal visibility
  const [newTemplateVariable, setNewTemplateVariable] = useState({
    templateVariableName: '',
    templateVariableDesc: '',
    templateVariableDefaultValue: '',
    status: true, // Default value
  });
  const [editingTemplateVariable, setEditingTemplateVariable] = useState(null); // Track which template variable is being edited
  const [editDescription, setEditDescription] = useState(''); // Track edited description
  const [editDefaultValue, setEditDefaultValue] = useState(''); // Track edited default value

  // State for filters
  const initialFilters = {
    id: null,
    templateVariableName: '',
    templateVariableDefaultValue: '',
    status: null
  };
  const [filters, setFilters] = useState(initialFilters);
  
  const resetFilters = () => {
    clearMessages();
    setFilters(initialFilters);
  };

  //for handling pagination
  const [page, setPage] = useState(0); // Starting from the first page
  const [size, setSize] = useState(3); // Number of items per page
  const [totalPages, setTotalPages] = useState(0); // To store total pages
  const [formError, setFormError] = useState({});
  const [editFormError, setEditFormError] = useState({});
  const [actionErrors, setActionErrors] = useState({});
  const [createError, setCreateError] = useState(null);
  const [createMessage, setCreateMessage] = useState(null);

  const resetEditForm = () => {
  setEditingTemplateVariable(null);
  setEditDescription('');
  setEditDefaultValue('');
  };

  const clearMessages = () => {
    setCreateError(null);
    setCreateMessage(null);
    setActionErrors({});
    setFormError({});
    setEditFormError({});
    resetEditForm();
  };

  useEffect(() => {

    const fetchTemplateVariables = async () => {
      try {
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_ALL_WA_TEMPLATE_VARIABLES_BY_PAGE_ENDPOINT,
          {
            params: {
              ...filters,
              page,
              size,
            },
          }
        );
        // Extract data from the response
        const { message, object, httpStatus } = response.data;

        if (httpStatus === 200) { // Check for successful status
          setTemplateVariables(object.content); // Set the templates from the response object
          setTotalPages(object.totalPages);
        } else {
          setError(`Error: ${message}`); // Handle non-success status code
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching templateVariables:', err);
        setError(err.message || 'An error occurred.');
        setLoading(false);

      }
    };

    fetchTemplateVariables();
  }, [waApi, page, size]);

  useEffect(() => {
    if (modalOpen) {
      setFormError({});
      setEditFormError({});
    }
  }, [modalOpen]);
  
  const handleInputChange = (e) => {
    // const { name, value } = e.target;
    // setNewTemplateVariable((prev) => ({
    //   ...prev,
    //   [name]: value,
    // }));
    const { name, type, checked, value } = e.target;

    setNewTemplateVariable((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value, // Use checked for checkbox type
    }));
  };

  const handleEditClick = (templateVariable) => {
    clearMessages();
    // Set the template being edited
    setEditingTemplateVariable(templateVariable.id);
    setEditDescription(templateVariable.templateVariableDesc); // Set the description to edit
    setEditDefaultValue(templateVariable.templateVariableDefaultValue); //set the default vaule to edit
  };

  const handleEditSubmit = async (templateVariableId) => {

    setCreateError(null);
    setCreateMessage(null);
    setActionErrors((prev) => ({ ...prev, [templateVariableId]: null }));
    setEditFormError({});
    const editPayload = {
      templateVariableDesc: editDescription,
      templateVariableDefaultValue: editDefaultValue
    };
    const validation = validateRequiredFields({
        templateVariableDesc: {
          value: editPayload.templateVariableDesc,
          label: "Variable Description"
        },
        templateVariableDefaultValue: {
          value: editPayload.templateVariableDefaultValue,
          label: "Variable Default Value"
        }
    });
    let errors = { ...validation.errors };
        if (Object.keys(errors).length > 0) {
      setEditFormError(errors);
      return;
    }
    setEditFormError({});
    try {
      // Update template variable with PUT request
      const trimmedEditPayload = deepTrim(editPayload);
      const response = await waApi.put(
        `${process.env.REACT_APP_PUT_WA_TEMPLATE_VARIABLE_ENDPOINT}/${templateVariableId}`,
        trimmedEditPayload
      );

      // Refresh the templateVariables list after the update
      const updatedTemplateVariables = templateVariables.map((templateVariable) => {
        if (templateVariable.id === templateVariableId) {
          return { ...templateVariable, templateVariableDesc: trimmedEditPayload.templateVariableDesc, templateVariableDefaultValue: trimmedEditPayload.templateVariableDefaultValue }; // Update the edited template variable
        }
        return templateVariable; // Keep the other records unchanged
      });
      setTemplateVariables(updatedTemplateVariables);
      setCreateMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [templateVariableId]: err.message || 'An error occurred while updating the Template variable.' }));
    } finally {
      resetEditForm();
    }
  };

  const handleSubmit = async (e) => {

    e.preventDefault();
    clearMessages();
    const validation = validateRequiredFields({
        variableName: {
          value: newTemplateVariable.templateVariableName,
          label: "Variable Name"
        },
        variableDefaultValue: {
          value: newTemplateVariable.templateVariableDefaultValue,
          label: "Variable Default Value"
        },
        variableDesc: {
          value: newTemplateVariable.templateVariableDesc,
          label: "Variable Description"
        }
    });
    let errors = { ...validation.errors };
        if (Object.keys(errors).length > 0) {
      setFormError(errors);
      return;
    }
    try {
      const payload = deepTrim(newTemplateVariable);
      const response = await waApi.post(
        `${process.env.REACT_APP_SET_WA_TEMPLATE_VARIABLE_ENDPOINT}`,
        payload
      );
      if (response.data.httpStatus === 200) {
        setTemplateVariables((prev) => [...prev, response.data.object]); // Update with new templateVariable
        setNewTemplateVariable({
          templateVariableName: '',
          templateVariableDesc: '',
          templateVariableDefaultValue: '',
          status: true, // Default value
        });
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while creating the templateVariable.');
    } finally {
      setModalOpen(false); // Close modal after submission
    }
  };

  const handleFilterInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const newFilters = { ...prev, [name]: value };
      return newFilters;
    });
  };

  // Handle the status change to convert to boolean
  const handleStatusChange = (e) => {
    const value = e.target.value;
    const statusValue = value === 'Active' ? true : value === 'Inactive' ? false : null;

    setFilters((prev) => {
      const newFilters = { ...prev, status: statusValue };
      return newFilters;
    });
  };

  const refreshCache = async () => {
    clearMessages();
    try {
      const response = await waApi.get(
        process.env.REACT_APP_REFRESH_WA_TEMPLATE_VARIABLE_CACHE_ENDPOINT
      );
      if (response.data.httpStatus === 200) {
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while refreshing the cache.');
    }
  };

  const toggleActivationStatus = async (templateVariableId, currentStatus) => {

    clearMessages();
    try {
      const newStatus = !currentStatus; // Toggle the current status
      const response = await waApi.patch(
        `${process.env.REACT_APP_SET_WA_TEMPLATE_VARIABLE_STATUS_ENDPOINT}/${templateVariableId}`,
        { status: newStatus }
      );
      setTemplateVariables((prev) =>
        prev.map((templateVariable) => templateVariable.id === templateVariableId ? { ...templateVariable, status: newStatus } : templateVariable)
      );
      setCreateMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [templateVariableId]: err.message || 'An error occurred while updating the status.' }));
    }
  };

  // New function to apply filters
  const applyFilters = async (pageParam) => {

    setLoading(true); // Show loading while fetching filtered notifications
    clearMessages();

    var hitApplyFilter = null;
    if(page === 0) {
      hitApplyFilter = true;
    } else {
      hitApplyFilter = false;
    }
    if(pageParam===0) {
      setPage(0);
    }
    if(hitApplyFilter) {
      try {
         const cleanedFilters = Object.fromEntries(
          Object.entries(filters).map(([key, value]) => [
            key,
            value === '' ? null : value,
          ])
        );
        const response = await waApi.get(
          process.env
            .REACT_APP_GET_ALL_WA_TEMPLATE_VARIABLES_BY_PAGE_ENDPOINT,
          {
            params: {
              ...cleanedFilters,
              page,
              size,
            },
          }
        );
      // Extract data from the response
      const { message, object, httpStatus } = response.data;

      if (httpStatus === 200) { // Check for successful status
        setTemplateVariables(object.content); // Set the templates from the response object
        setTotalPages(object.totalPages);
      } else {
        setCreateError(message); // Handle non-success status code
      }

      setLoading(false);
    } catch (err) {
        console.error('Error fetching templateVariables:', err);
        setCreateError(err.message || 'An error occurred.');
        setLoading(false);
      }
    }
  };

  const handleNextPage = () => {
    clearMessages();
    if (page + 1 < totalPages) {
      setPage((prevPage) => prevPage + 1); // Increment page number
    }
  };

  const handlePreviousPage = () => {
    clearMessages();
    if (page > 0) {
      setPage((prevPage) => prevPage - 1); // Decrement page number
    }
  };

  const handleSizeChange = (e) => {
    clearMessages();
    setSize(e.target.value);
    setPage(0); // Reset to first page on changing size
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="page-container">
      <h1>App Template Variables</h1>
      <div className="flex-row filter-section">
        <div className='filter-item'>
          <label htmlFor="items-per-page">Items per page: </label>
          <select id="items-per-page" onChange={handleSizeChange} className="w-auto">
            <option value={3}>default</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={5}>5</option>
            <option value={10}>10</option>
          </select>
        </div>
      </div>

      <h3>Filters</h3>
      {/* Filter Form */}
      <div className="flex-row filter-section">
        <div className="filter-item">
          <label>Id:</label>
          <input
            type="text"
            name="id"
            value={filters.id ?? ''}
            onChange={handleFilterInputChange}
            placeholder="Only numeric value"
          />
        </div>

        <div className="filter-item">
          <label>Name:</label>
          <input
            type="text"
            name="templateVariableName"
            value={filters.templateVariableName ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>DefaultValue:</label>
          <input
            type="text"
            name="templateVariableDefaultValue"
            value={filters.templateVariableDefaultValue ?? ''}
            onChange={handleFilterInputChange}
          />
        </div>

        <div className="filter-item">
          <label>Status:</label>
          <select name="status" onChange={handleStatusChange} value={filters.status === null ? '' : filters.status ? 'Active' : 'Inactive'}>
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <button type="button" onClick={() => applyFilters(0)}>Apply Filters</button>
        <button type="button" onClick={resetFilters}> Reset Filters</button>

      </div>

      {/* Button to open the modal */}
      <div className="action-bar">
        <button
          onClick={() => {
            clearMessages();
            setNewTemplateVariable({
              templateVariableName: '',
              templateVariableDesc: '',
              templateVariableDefaultValue: '',
              status: true, // Default value
            });
            setModalOpen(true);
          }}
        >
          + Create New
        </button>

        <button type="button" onClick={refreshCache}>
          Refresh Cache
        </button>
      </div>

      {createMessage && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createMessage}
        </div>
      )}

      {createError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createError}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => { clearMessages(); setModalOpen(false); }}>
        <div className="page-container">
          <h3>Create New Template Variable</h3>
          {formError && Object.values(formError).some(val => val !== null && val !== undefined && val !== "") && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "12px", borderRadius: "6px", marginBottom: "16px" }}>
              <strong>Please fix the following errors:</strong>
              <ul style={{ margin: "8px 0 0 20px" }}>
                {Object.entries(formError)
                  .filter(([_, val]) => val !== null && val !== undefined && val !== "")
                  .map(([key, err], index) => (
                    <li key={index}>
                      {typeof err === "object" ? JSON.stringify(err) : String(err)}
                    </li>
                  ))}
              </ul>
            </div>
          )}
          <form onSubmit={handleSubmit} className="standard-form">

            <div className="form-group">
              <label>Name:</label>
              <input
                type="text"
                name="templateVariableName"
                value={newTemplateVariable.templateVariableName}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Description:</label>
              <input
                type="text"
                name="templateVariableDesc"
                value={newTemplateVariable.templateVariableDesc}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Default value:</label>
              <input
                type="text"
                name="templateVariableDefaultValue"
                value={newTemplateVariable.templateVariableDefaultValue}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>Activation Status:</label>
              <input
                type="checkbox"
                name="status"
                checked={newTemplateVariable.status}
                onChange={handleInputChange}
              />
            </div>

            <button type="submit">Create Category</button>
          </form>
        </div>
      </Modal>

      <h3>Existing App Template Variables</h3>
      {templateVariables.length === 0 ? (
        <p>No Template Variables available</p>
      ) : (
        <div className="table-responsive">
        {editFormError && Object.values(editFormError).some(val => val !== null && val !== undefined && val !== "") && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "12px", borderRadius: "6px", marginBottom: "16px" }}>
              <strong>Please fix the following errors:</strong>
              <ul style={{ margin: "8px 0 0 20px" }}>
                {Object.entries(editFormError)
                  .filter(([_, val]) => val !== null && val !== undefined && val !== "")
                  .map(([key, err], index) => (
                    <li key={index}>
                      {typeof err === "object" ? JSON.stringify(err) : String(err)}
                    </li>
                  ))}
              </ul>
            </div>
          )}
          <table className="notifications-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Description</th>
                <th>Default</th>
                <th>Created At</th>
                <th>Status</th>
                <th>Last Updated At</th>
                <th>Last Updated By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templateVariables?.map((templateVariable) => (
                <tr key={templateVariable.id}>
                  <td>{templateVariable.id}</td>
                  <td>{templateVariable.templateVariableName}</td>
                  <td>
                    {editingTemplateVariable === templateVariable.id ? (
                      <input
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)} // Handle edit description change
                      />
                    ) : (
                      templateVariable.templateVariableDesc
                    )}
                  </td>
                  <td>
                    {editingTemplateVariable === templateVariable.id ? (
                      <input
                        type="text"
                        value={editDefaultValue}
                        onChange={(e) => setEditDefaultValue(e.target.value)} // Handle edit default value change
                      />
                    ) : (
                      templateVariable.templateVariableDefaultValue
                    )}
                  </td>
                  <td>{new Date(templateVariable.createdTimestamp).toLocaleString()}</td>
                  <td>{templateVariable.status ? 'Active' : 'Inactive'}</td>
                  <td>{new Date(templateVariable.updatedTimestamp).toLocaleString()}</td>
                  <td>{templateVariable.lastUpdatedBy}</td>
                  <td>
                    <div className="action-buttons">
                      {editingTemplateVariable === templateVariable.id ? (
                        <>
                          <button onClick={() => handleEditSubmit(templateVariable.id)}>Save</button> 
                          <button type="button" onClick={clearMessages}>Cancel</button>
                        </>
                      ) : (
                          <button onClick={() => handleEditClick(templateVariable)}>Edit</button> // Set whatsapp template variable to edit
                      )}
                      <button onClick={() => toggleActivationStatus(templateVariable.id, templateVariable.status)}>
                        {templateVariable.status ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                    {actionErrors[templateVariable.id] && (
                      <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                        {actionErrors[templateVariable.id]}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Pagination Controls */}
      <div className="pagination-container">
        <button onClick={handlePreviousPage} disabled={page === 0}>Previous</button>
        <span>Page {page + 1} of {totalPages}</span>
        <button onClick={handleNextPage} disabled={page + 1 === totalPages}>Next</button>
      </div>
    </div>
  );
};

export default WaTemplateVariablePage;
